// @view/authentication/Login.view.tsx

import React from "react";
import * as styles from "@styles/Login.css";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import EmptyView from "@view/EmptyView";

import * as Option from "@utils/Option";

import type { LoginViewProps } from "@ps-bridge/contracts/login";
import * as IO from "@ps-bridge/interop/io";
import type { OptionTs } from "@ps-bridge/interop";
import { ok } from "@ps-bridge/ok";

type LoginViewPropsWithState = LoginViewProps & {
    pageState: "init" | "ready";
    ssoErrorMessage: OptionTs<string>;
};

const providerSsoStartUrl = (googleSsoStartUrl: string, provider: "microsoft" | "apple"): string =>
    googleSsoStartUrl.replace("/v1/auth/google/start", `/v1/auth/${provider}/start`);

const LoginView: React.FC<LoginViewPropsWithState> = ({
    emailValue: _emailValue,
    passwordValue: _passwordValue,
    onEmailChange: _onEmailChange,
    onPasswordChange: _onPasswordChange,
    onSubmit,
    onNavigateToCreateAccount,
    onNavigateToPasswordReset,
    logoUri,
    googleSsoStartUrl,
    pageState,
    ssoErrorMessage,
}) => {
    const microsoftSsoStartUrl = providerSsoStartUrl(googleSsoStartUrl, "microsoft");
    const appleSsoStartUrl = providerSsoStartUrl(googleSsoStartUrl, "apple");

    return (
        <div className={styles.page} data-testid="page-login" data-page-state={pageState}>
            <Card className={styles.card}>
                <CardContent>
                    {Option.fold(logoUri, () => <EmptyView />, (src: string) => <img className={styles.logo} src={src} alt="Ouroboros Logo" />,)}
                    <CardTitle className={styles.title}>
                        Ouroboros AI's Ouroborite Login
                    </CardTitle>
                    {Option.fold(ssoErrorMessage, () => <EmptyView />, (message) => (
                        <div className={styles.errorBanner} role="alert">
                            {message}
                        </div>
                    ))}
                    <div className={styles.field}>
                        <Input
                            type="email"
                            placeholder="Email"
                            autoComplete="email"
                            data-testid="login-email"
                        />
                    </div>
                    <div className={styles.field}>
                        <Input
                            type="password"
                            placeholder="Password"
                            autoComplete="current-password"
                            data-testid="login-password"
                        />
                    </div>
                    <Button
                        className={styles.button}
                        onClick={() => {
                            console.log("Login button clicked");
                            IO.run(onSubmit(ok));
                        }}
                        asChild={Option.none()}
                        data-testid="login-submit"
                    >
                        Login
                    </Button>

                    <Button
                        variant="outline"
                        className={styles.button}
                        asChild={Option.some(true)}
                        data-testid="login-google-sso"
                    >
                        <a href={googleSsoStartUrl}>Continue with Google</a>
                    </Button>
                    <Button
                        variant="outline"
                        className={styles.button}
                        asChild={Option.some(true)}
                        data-testid="login-microsoft-sso"
                    >
                        <a href={microsoftSsoStartUrl}>Continue with Microsoft</a>
                    </Button>
                    <Button
                        variant="outline"
                        className={styles.button}
                        asChild={Option.some(true)}
                        data-testid="login-apple-sso"
                    >
                        <a href={appleSsoStartUrl}>Continue with Apple</a>
                    </Button>

                    <Button
                        variant="ghost"
                        className={styles.textButton}
                        onClick={() => IO.run(onNavigateToPasswordReset(ok))}
                        asChild={Option.none()}
                        data-testid="login-forgot-password"
                    >
                        Forgot your password?
                    </Button>

                    <p className={styles.signUpText}>Don't have an account?</p>
                    <Button
                        variant="ghost"
                        className={styles.textButton}
                        onClick={() => IO.run(onNavigateToCreateAccount(ok))}
                        asChild={Option.none()}
                        data-testid="login-sign-up"
                    >
                        Sign Up
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};

export default LoginView;
